export const RealtimeConnectionState = Object.freeze({
  DISCONNECTED: "Disconnected",
  AUTHORIZING: "Authorizing",
  CONNECTING: "Connecting",
  CONNECTED: "Connected",
  RECONNECTING: "Reconnecting",
  DEGRADED: "Degraded",
  UNAUTHORIZED: "Unauthorized",
  CLOSED: "Closed"
});

export const RealtimeSubscriptionType = Object.freeze({
  PRICEDATA: "PRICEDATA",
  QUOTE: "QUOTE",
  TRADE: "TRADE"
});

export const ProviderSubscriptionStatus = Object.freeze({
  PENDING: "pending",
  SUBSCRIBED: "subscribed",
  UNENTITLED: "unentitled",
  REJECTED: "rejected",
  INVALID_SYMBOL: "invalid-symbol"
});

export function availabilityFromProviderEntitlement(value) {
  const entitlement = String(value || "").trim().toUpperCase();
  if (!entitlement) return null;
  if (entitlement.startsWith("DL") || entitlement.includes("DELAY")) return "delayed";
  if (entitlement.startsWith("RT") || entitlement.includes("REALTIME")) return "live";
  return null;
}

export function normalizeSubscriptionRequest(request) {
  if (!request || typeof request !== "object") {
    throw new TypeError("A real-time subscription request is required.");
  }
  const internalSymbolId = String(request.internalSymbolId || "").trim();
  const providerSymbol = String(request.providerSymbol || "").trim().toUpperCase();
  const type = String(request.type || "").trim().toUpperCase();
  if (!internalSymbolId || !providerSymbol || !Object.hasOwn(RealtimeSubscriptionType, type)) {
    throw new TypeError("A subscription requires canonical identity, provider symbol, and supported type.");
  }
  const conflation = request.conflation === null || request.conflation === undefined
    ? null
    : String(request.conflation);
  return Object.freeze({
    internalSymbolId,
    providerSymbol,
    type,
    conflation,
    skipHeavyInitialLoad: request.skipHeavyInitialLoad === true,
    exchange: String(request.exchange || "").trim().toUpperCase() || null,
    expectedAvailability: String(request.expectedAvailability || "").trim() || null
  });
}

export function subscriptionKey(request) {
  const value = normalizeSubscriptionRequest(request);
  return [
    value.internalSymbolId,
    value.providerSymbol,
    value.type,
    value.conflation ?? "default",
    value.skipHeavyInitialLoad ? "light" : "heavy"
  ].join("|");
}

export function normalizeQuoteMediaMessage(raw, resolveType, receivedAt = Date.now()) {
  if (!raw || typeof raw !== "object") return null;
  let resolved;
  try {
    resolved = resolveType?.(raw);
  } catch {
    return null;
  }
  const type = String(resolved || raw.type || raw.messageType || "").toUpperCase();
  if (!Object.hasOwn(RealtimeSubscriptionType, type)) return null;
  const providerSymbol = String(raw.symbol || raw.key || "").trim().toUpperCase();
  if (!providerSymbol) return null;
  const eventTimestampMs = providerTimestamp(
    type === RealtimeSubscriptionType.PRICEDATA
      ? raw.lastTradeTime ?? raw.timestamp ?? raw.time ?? raw.date
      : raw.timestamp ?? raw.time ?? raw.tradeTime ?? raw.date
  );
  const sequence = stringOrNull(
    raw.sequenceNumber ?? raw.sequencenumber ?? raw.sequence ?? raw.seq
  );
  const providerEventId = stringOrNull(
    raw.matchID ?? raw.matchId ?? raw.id ?? raw.eventId ?? raw.tradeId
  );
  const price = finiteNumber(
    type === RealtimeSubscriptionType.TRADE
      ? raw.price ?? raw.last
      : raw.last ?? raw.price
  );
  const message = {
    provider: "QuoteMedia",
    providerSymbol,
    exchange: stringOrNull(
      raw.excode ?? raw.lastTradeExcode ?? raw.exchange ?? raw.market ?? raw.exch ??
      raw.bidExcode ?? raw.askExcode
    ),
    bidExchange: stringOrNull(raw.bidExcode),
    askExchange: stringOrNull(raw.askExcode),
    lastTradeExchange: stringOrNull(raw.lastTradeExcode ?? raw.excode),
    type,
    eventTimestampUtc: eventTimestampMs === null
      ? null
      : new Date(eventTimestampMs).toISOString(),
    eventTimestampMs,
    providerSequence: sequence,
    providerEventId,
    receiptTimestampUtc: new Date(receivedAt).toISOString(),
    receiptTimestampMs: receivedAt,
    price,
    size: finiteNumber(raw.size ?? raw.lastTradeSize ?? raw.lastSize ?? raw.tradeSize),
    bid: finiteNumber(raw.bid ?? raw.bidPrice),
    ask: finiteNumber(raw.ask ?? raw.askPrice),
    bidSize: finiteNumber(raw.bidSize ?? raw.bidsize),
    askSize: finiteNumber(raw.askSize ?? raw.asksize),
    volume: finiteNumber(raw.accumulatedVolume ?? raw.volume ?? raw.totalVolume),
    change: finiteNumber(raw.change ?? raw.netChange),
    percentChange: finiteNumber(raw.percentChange ?? raw.changePercent ?? raw.pctChange),
    session: stringOrNull(raw.session ?? raw.marketSession),
    entitlement: stringOrNull(raw.entitlement),
    tradeCondition: Object.freeze({
      indicator: stringOrNull(raw.indicator),
      rangeIndicator: stringOrNull(raw.rangeIndicator),
      tick: stringOrNull(raw.tick),
      flags: finiteNumber(raw.flags)
    }),
    providerTiming: Object.freeze({
      sourceTimestamp: stringOrNull(raw.timestamp),
      lastTradeTimestamp: stringOrNull(raw.lastTradeTime),
      preMarketTimestamp: stringOrNull(raw.preMarketTradeTime),
      postMarketTimestamp: stringOrNull(raw.postMarketTradeTime),
      delay: finiteNumber(raw.delay ?? raw.latency)
    })
  };
  return Object.freeze(message);
}

function providerTimestamp(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 10_000_000_000 ? value * 1000 : value;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric) && String(value).trim() !== "") {
    return numeric < 10_000_000_000 ? numeric * 1000 : numeric;
  }
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function finiteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function stringOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
}
